import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AppShell } from "../components/cod/Layout";
import { ModuleUnderConfiguration } from "../components/cod/ModulePlaceholder";
import { Seo } from "../components/cod/Seo";
import { useAuth } from "../lib/auth";
import { docsApi, msgApi, notifApi, bdApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Briefcase, Users, FileText, Mail, Bell, TrendingUp, ClipboardList, Handshake, Building2, GraduationCap, Landmark, HeartHandshake } from "lucide-react";

// ─── Shared portal utilities ────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, testid }) {
  return (
    <Card data-testid={testid} className="border-slate-200">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
            <div className="text-2xl font-serif text-slate-900 mt-1">{value ?? "—"}</div>
          </div>
          {Icon && <div className="rounded-md bg-slate-100 p-2"><Icon size={18} className="text-[var(--cod-navy)]"/></div>}
        </div>
      </CardContent>
    </Card>
  );
}

function PortalHeader({ title, subtitle, orgName }) {
  return (
    <div className="mb-8">
      {orgName && <div className="text-xs uppercase tracking-widest text-[var(--cod-red)] font-semibold">{orgName}</div>}
      <h1 className="font-serif text-3xl text-slate-900 mt-1">{title}</h1>
      <div className="cod-rule-red mt-3"/>
      {subtitle && <p className="text-slate-600 mt-3">{subtitle}</p>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// EMPLOYER PORTAL — moved to EmployerPortal.jsx (Phase 2 real modules)
// ═══════════════════════════════════════════════════════════════════════════


// ═══════════════════════════════════════════════════════════════════════════
// PARTNER PORTAL
// ═══════════════════════════════════════════════════════════════════════════
export function PartnerDashboard() {
  const { user } = useAuth();
  const [counts, setCounts] = useState({});
  useEffect(() => {
    Promise.all([
      docsApi.mine().catch(()=>[]),
      msgApi.mine().catch(()=>[]),
      notifApi.mine().catch(()=>[]),
    ]).then(([d, m, n]) => setCounts({ docs: d.length, unread: m.filter(x=>!x.read && x.to_user_id===user.id).length, notifs: n.filter(x=>!x.read).length }));
  }, [user.id]);
  return (
    <AppShell>
      <Seo title="Partner Portal" path="/portal/partner" noindex/>
      <PortalHeader
        title="Partner Portal"
        subtitle="Coordinate referrals, share resources, and track partnership activities with COD."
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="partner-stats">
        <StatCard icon={Handshake} label="Active Referrals" value={0} testid="stat-partner-referrals"/>
        <StatCard icon={ClipboardList} label="Programs" value={0} testid="stat-partner-programs"/>
        <StatCard icon={FileText} label="Documents" value={counts.docs} testid="stat-partner-docs"/>
        <StatCard icon={Mail} label="Unread Messages" value={counts.unread || 0} testid="stat-partner-msgs"/>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Link to="/partner/referrals" className="rounded-lg border border-slate-200 bg-white p-5 hover:border-[var(--cod-red)]">
          <Handshake className="text-[var(--cod-navy)] mb-2" size={20}/>
          <div className="font-semibold text-slate-900">Referrals</div>
          <div className="text-xs text-slate-600 mt-1">Review and act on referrals sent by COD staff.</div>
        </Link>
        <Link to="/portal/partner/messages" className="rounded-lg border border-slate-200 bg-white p-5 hover:border-[var(--cod-red)]">
          <Mail className="text-[var(--cod-navy)] mb-2" size={20}/>
          <div className="font-semibold text-slate-900">Messages</div>
          <div className="text-xs text-slate-600 mt-1">Referral-scoped messaging with the assigned COD Case Manager.</div>
        </Link>
      </div>
    </AppShell>
  );
}

export function PartnerPrograms() {
  return (
    <AppShell>
      <Seo title="Partner · Programs" path="/portal/partner/programs" noindex/>
      <ModuleUnderConfiguration
        testid="module-partner-programs"
        title="Program Coordination"
        purpose="Coordinate joint programs, events, and shared cohorts with Chicago Opportunity Desk. Log program outcomes, share attendance, and report joint metrics."
        capabilities={[
          "Joint program calendar and event coordination",
          "Cohort roster sharing (with participant consent)",
          "Outcome reporting for grant compliance",
          "Shared resource library",
        ]}
        eta="Q1 2026."
        fallback={[
          { to: "/partner/referrals", label: "Referrals", desc: "Actively route participants to COD services." },
        ]}
      />
    </AppShell>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// TRAINING PROVIDER PORTAL
// ═══════════════════════════════════════════════════════════════════════════
export function TrainingDashboard() {
  return (
    <AppShell>
      <Seo title="Training Provider Portal" path="/portal/training" noindex/>
      <PortalHeader
        title="Training Provider Portal"
        subtitle="Deliver certifications and training pathways for COD participants. Manage cohorts, attendance, and completion outcomes."
      />
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="training-stats">
        <StatCard icon={GraduationCap} label="Active Cohorts" value={0} testid="stat-train-cohorts"/>
        <StatCard icon={Users} label="Enrolled Participants" value={0} testid="stat-train-enrolled"/>
        <StatCard icon={FileText} label="Documents" value={0} testid="stat-train-docs"/>
        <StatCard icon={Mail} label="Unread Messages" value={0} testid="stat-train-msgs"/>
      </div>
      <ModuleUnderConfiguration
        testid="module-training-cohorts"
        title="Cohort Management"
        purpose="Configure certification cohorts, publish schedules to COD staff, and track participant attendance and completion. Certificates automatically deposit into the participant's Document Vault."
        capabilities={[
          "Publish upcoming certification dates (CPR, CNA, Food Manager, PERC, etc.)",
          "Accept COD staff enrollments directly",
          "Attendance tracking with pass/fail recording",
          "Auto-issue completion certificates to participant vaults",
        ]}
        eta="Activates after your training partner MOU is signed."
      />
    </AppShell>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// GOVERNMENT AGENCY PORTAL
// ═══════════════════════════════════════════════════════════════════════════
export function GovernmentDashboard() {
  return (
    <AppShell>
      <Seo title="Government Portal" path="/portal/government" noindex/>
      <PortalHeader title="Government Agency Portal" subtitle="Coordinate contracts, workforce initiatives, and grant partnerships with Chicago Opportunity Desk."/>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="gov-stats">
        <StatCard icon={Landmark} label="Active Contracts" value={0} testid="stat-gov-contracts"/>
        <StatCard icon={FileText} label="Compliance Docs" value={0} testid="stat-gov-docs"/>
        <StatCard icon={Users} label="Served (YTD)" value={0} testid="stat-gov-served"/>
        <StatCard icon={Mail} label="Messages" value={0} testid="stat-gov-msgs"/>
      </div>
      <ModuleUnderConfiguration
        testid="module-gov-contracts"
        title="Contract & Grant Coordination"
        purpose="Track active contracts and grant deliverables between your agency and COD. Compliance uploads, deliverable milestones, and outcome reporting in one place."
        capabilities={[
          "Contract lifecycle tracking with milestones",
          "Compliance document repository with expiration alerts",
          "Outcome dashboards aligned to your grant metrics",
          "Direct messaging to the COD Executive team",
        ]}
      />
    </AppShell>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// FUNDING ORGANIZATION PORTAL
// ═══════════════════════════════════════════════════════════════════════════
export function FundingDashboard() {
  return (
    <AppShell>
      <Seo title="Funding Portal" path="/portal/funding" noindex/>
      <PortalHeader title="Funding Organization Portal" subtitle="Support Chicago Opportunity Desk programs through grants, foundation gifts, or investment. Track program impact against your funding priorities."/>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="funding-stats">
        <StatCard icon={Landmark} label="Active Grants" value={0} testid="stat-fund-grants"/>
        <StatCard icon={TrendingUp} label="Impact Metrics" value="—" testid="stat-fund-impact"/>
        <StatCard icon={FileText} label="Reports" value={0} testid="stat-fund-reports"/>
        <StatCard icon={Mail} label="Messages" value={0} testid="stat-fund-msgs"/>
      </div>
      <ModuleUnderConfiguration
        testid="module-fund-grants"
        title="Grants & Impact Reporting"
        purpose="Track active grants, review structured impact dashboards, and receive quarterly reports directly from the COD Executive team."
        capabilities={[
          "Active grant roster with milestones and deliverables",
          "Real-time impact dashboards (participants served, credentials awarded, employment outcomes)",
          "Downloadable quarterly and annual reports",
          "Direct dialogue with the COD Executive Director",
        ]}
      />
    </AppShell>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// VOLUNTEER PORTAL
// ═══════════════════════════════════════════════════════════════════════════
export function VolunteerDashboard() {
  return (
    <AppShell>
      <Seo title="Volunteer Portal" path="/portal/volunteer" noindex/>
      <PortalHeader title="Volunteer Portal" subtitle="Support the COD mission through mentorship, events, and community work."/>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="volunteer-stats">
        <StatCard icon={HeartHandshake} label="Open Opportunities" value={0} testid="stat-vol-opps"/>
        <StatCard icon={Users} label="Hours (YTD)" value={0} testid="stat-vol-hours"/>
        <StatCard icon={FileText} label="Waivers" value={0} testid="stat-vol-docs"/>
        <StatCard icon={Mail} label="Messages" value={0} testid="stat-vol-msgs"/>
      </div>
      <ModuleUnderConfiguration
        testid="module-vol-opps"
        title="Volunteer Opportunities"
        purpose="Browse upcoming volunteer opportunities — mentorship sessions, mock interviews, community events, and skills workshops. Sign up, log hours, and receive year-end contribution letters."
        capabilities={[
          "Opportunity calendar with skill-matched suggestions",
          "One-click sign-up and hour logging",
          "Year-end volunteer contribution letters",
          "Background-check status tracking (for mentorship roles)",
        ]}
      />
    </AppShell>
  );
}


// ═══════════════════════════════════════════════════════════════════════════
// Fallback modules — Documents / Messages / Notifications / Settings
// Reuse the existing production-ready pages by embedding the DashboardPage
// they need behind the AppShell of the currently-active portal role.
// The Dashboard.jsx modules already handle these — we just wrap them.
// ═══════════════════════════════════════════════════════════════════════════
export { DocumentsPage as PortalDocuments, MessagesPage as PortalMessages } from "./Dashboard";
